import type { FunctionComponent } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';

import { icons, IconText } from '../../../generic/Icon';
import type { ProjectGroupRelease, ProjectGroupReleaseProject } from '../../../../types';
import ProjectsModule from '../../../../redux/projects';
import styles from './ProjectGroupReleaseProjectsTableHeader.module.less';
import type { ProjectEntry } from '../../../../redux/projects/store';
import { getLatestRevision } from '../helper';

type Props = {
  releaseProjects: ProjectGroupReleaseProject[];
  showSelectProjects?: boolean;
  onShowSelectProjects?: () => void;
  editProjects?: boolean;
  projectGroupId?: number;
  selectedRelease?: ProjectGroupRelease;
  onUpdateProject?: (project: ProjectGroupReleaseProject) => void;
};

const ProjectGroupReleaseProjectsTableHeader: FunctionComponent<Props> = ({
  releaseProjects,
  showSelectProjects,
  onShowSelectProjects,
  editProjects,
  projectGroupId,
  selectedRelease,
  onUpdateProject,
}) => {
  const allProjects: Record<string, ProjectEntry> = useSelector(ProjectsModule.selectors.getProjects);

  const editRedirect = selectedRelease
    ? `/projects/group/${projectGroupId}/releases/${selectedRelease.id}/edit`
    : `/projects/group/${projectGroupId}/releases/`;

  const areAllLatestRevisionsSelected = releaseProjects.every((releaseProject) => {
    const latestRevision = getLatestRevision(allProjects, releaseProject);
    return releaseProject.revision?.locator === latestRevision?.locator;
  });

  const areAnyRevisionsLoading = Object.values(allProjects).some((project) => project.isFetchingRevisions);

  const selectLatestRevisions = () => {
    if (!onUpdateProject) {
      return;
    }

    releaseProjects.forEach((releaseProject) => {
      const latestRevision = getLatestRevision(allProjects, releaseProject);
      onUpdateProject({
        ...releaseProject,
        revision: latestRevision,
      });
    });
  };

  return (
    <div className="card-header">
      <div className={styles.header}>
        <div className={styles.count}>
          Projects In Release{' '}
          <Badge>{releaseProjects.length || 0}</Badge>
        </div>
        {!editProjects && (
          <div>
            <Button onClick={selectLatestRevisions} disabled={areAllLatestRevisionsSelected || areAnyRevisionsLoading}>
              Select latest revision for all projects
            </Button>
          </div>
        )}
        {!editProjects && !showSelectProjects && (
          <div>
            <Button onClick={onShowSelectProjects}>
              <IconText
                icon={icons.plusCircle}
                text="Add More"
              />
            </Button>
          </div>
        )}
        {editProjects && (
          <div>
            <Link
              to={editRedirect}
              className="btn btn-default"
            >
              <IconText
                icon={icons.edit}
                text="Edit"
              />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectGroupReleaseProjectsTableHeader;
